import type { ModelExecution } from "@/shared/agent-display/model-execution";
import type { ToolTask, TurnStep } from "../api/use-agents";

export type { ToolTask } from "../api/use-agents";

/**
 * O QUE UM TURNO CUSTOU, e como se escrevem os números dele.
 *
 * Um módulo à parte do cartão que os desenha porque os mesmos números aparecem em
 * dois estados do mesmo turno — ao vivo e arquivado — e um `12.4k` formatado de
 * duas maneiras nas duas metades da mesma conversa é a espécie de detalhe que
 * ninguém reporta e toda a gente nota.
 */

/**
 * UMA FERRAMENTA, na forma que o cartão desenha.
 *
 * Vivia em `bubble.ts` e mudou-se para cá quando o cartão passou a ser o dono
 * dela: `bubble.ts` precisa do `TurnMeter` daqui, e este precisava do `LiveTool`
 * de lá — um ciclo, e o ciclo é sempre a fronteira no sítio errado. A fronteira
 * certa é esta: aqui está o TRABALHO do turno, lá está a FALA que ele deixa.
 */
export interface LiveTool {
	id: string;
	name: string;
	/** Com o QUE foi chamada: uma tool lenta só interessa se dá para ver o que ela
	 *  foi fazer. */
	input: string;
	output?: string;
	isError: boolean;
	ms?: number;
	/**
	 * O TRABALHO DENTRO DO PASSO, quando a ferramenta o reporta (`tool_tasks`):
	 * o filho do `spawn_agent` ferramenta a ferramenta, as telas-rascunho em
	 * paralelo. Cada quadro traz a lista INTEIRA — substitui, não soma.
	 */
	tasks?: readonly ToolTask[];
}

/** Quem respondeu, quanto custou, em quanto tempo. Chega no quadro `done`. */
export interface TurnMeter {
	readonly execution?: ModelExecution;
	readonly provider?: string;
	readonly model?: string;
	readonly costMicroUsd?: number;
	readonly durationMs?: number;
	/** `null` numa fala guardada por um turno sem créditos apurados (0148). */
	readonly credits?: number | null;
}

/** Os tokens do turno, separados. `cached` e `reasoning` chegavam no fio e eram
 *  somados num número só — o turno caro não sabia dizer por quê. */
export interface TurnTokens {
	readonly input: number;
	readonly output: number;
	readonly cached?: number;
	readonly reasoning?: number;
}

/**
 * Um passo ARQUIVADO na mesma forma de um AO VIVO.
 *
 * O cartão de atividade não deve saber se está a olhar para um turno a acontecer
 * ou para um que aconteceu ontem: são o mesmo facto em dois momentos, e dois
 * componentes para os desenhar é como um deles fica sem o selo de erro.
 */
export function toolsFromSteps(steps: readonly TurnStep[]): LiveTool[] {
	return steps.map((step, index) => ({
		id: String(index),
		name: step.tool,
		input: step.input ?? "",
		...(step.output !== undefined && step.output !== null ? { output: step.output } : {}),
		isError: failedStep(step),
		ms: step.ms,
		...(step.tasks?.length ? { tasks: step.tasks } : {}),
	}));
}

/**
 * Se um passo falhou.
 *
 * A mesma heurística do `ToolSteps`, e pela mesma razão: o servidor diz-no
 * (`isError`) desde que o corredor passou a persisti-lo, e quando diz, é essa a
 * resposta. O farejar de texto fica SÓ para os passos gravados antes de a
 * bandeira existir, que não trazem `isError` nenhum.
 */
export function failedStep(step: { isError?: boolean; output?: string | null }): boolean {
	if (step.isError !== undefined) return step.isError;
	return /^(error|could not|refus|unknown|no such|not allowed|permission denied|memory could not)/i.test(
		(step.output ?? "").trim(),
	);
}

/**
 * Uma duração na unidade que diz alguma coisa.
 *
 * Abaixo de um segundo os milissegundos SÃO a informação — uma leitura de 10ms e
 * uma de 900ms são factos diferentes sobre o que acabou de acontecer, e "0.0s"
 * para as duas lê-se como um relógio parado. Acima de um minuto a forma muda
 * outra vez: `2m05s`, como na V2, porque `125.3s` obriga a uma conta de cabeça.
 */
export function fmtElapsed(ms: number): string {
	if (ms < 1000) return `${ms}ms`;
	const seconds = Math.floor(ms / 1000);
	if (seconds < 60) return `${(ms / 1000).toFixed(1)}s`;
	return `${Math.floor(seconds / 60)}m${String(seconds % 60).padStart(2, "0")}s`;
}

/** Tokens compactos: `12.4k` acima de mil. Um número de cinco dígitos ao lado de
 *  outro de cinco dígitos não se compara de relance, que é para o que ele serve. */
export function fmtTokens(n: number): string {
	return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/**
 * O custo do turno em dólares, a partir dos micro-dólares que o servidor conta.
 *
 * QUATRO casas abaixo de um cêntimo, e não duas: um turno normal custa
 * US$0,0079, e duas casas escrevem "US$0,00" — que é a mesma coisa que não
 * mostrar o custo, com a agravante de parecer que é de graça.
 */
export function fmtCost(microUsd: number, locale: string): string {
	const usd = microUsd / 1_000_000;
	const digits = usd > 0 && usd < 0.01 ? 4 : 2;
	return `US$${usd.toLocaleString(locale, {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	})}`;
}

/**
 * O QUE O AGENTE FEZ, numa frase — e nunca o texto cru do modelo.
 *
 * A regra da V2, mantida à letra: no estado final e sem `reasoningRaw`, o
 * parágrafo do cartão é a lista de ações humanizadas, não o que o modelo pensou.
 * O raciocínio cru é um registo de trabalho; pô-lo em prosa debaixo de uma
 * resposta convida a lê-lo como parte dela.
 */
export function summarizeActions(labels: readonly string[]): string {
	const shown = labels.slice(0, 4).join(" · ");
	return shown ? `${shown}.` : "";
}

/**
 * O raciocínio cru, apresentável.
 *
 * A marcação de markdown sai porque isto é desenhado como texto e não como
 * markdown — um `**` literal no meio de um parágrafo lê-se como um erro da tela.
 */
export function cleanThought(text: string, limit = 320): string {
	const flat = text
		.replace(/```[\s\S]*?```/g, " ")
		.replace(/[*_`>#|]/g, "")
		.replace(/\s+/g, " ")
		.trim();
	return flat.length > limit ? `${flat.slice(0, limit - 1)}…` : flat;
}

/**
 * A janela do JSON cru com os escapes de string desfeitos — o HTML lê-se como
 * HTML, não como `\"` e `\n`. Barato de propósito: sem parsear JSON parcial;
 * um escape cortado no início da janela sai como veio, e é o fim que se lê.
 */
const ESCAPES: Record<string, string> = { n: "\n", t: "\t", r: "", '"': '"', "\\": "\\", "/": "/" };
export function readableJson(raw: string): string {
	return raw.replace(/\\(u[0-9a-fA-F]{4}|.)/g, (all, code: string) =>
		code.length === 5
			? String.fromCharCode(Number.parseInt(code.slice(1), 16))
			: (ESCAPES[code] ?? all),
	);
}
